import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";

import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";

import authRepository from "api/authRepository";
import { useAuth } from "contexts/authContext";

function SignIn() {
  const navigate = useNavigate();
  const toast = useToast();

  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  const { login } = useAuth();
  const pageBg = useColorModeValue('white', '#0B1437');
  const cardBg = useColorModeValue('white', '#111C44');
  const inputBg = useColorModeValue('#EEEBFA', '#1B254B');
  const textColor = useColorModeValue('#2D2A5E', 'white');
  const textColorDetails = useColorModeValue('#2D2A5E', 'whiteAlpha.700');
  const textColorBrand = useColorModeValue('#7062B9', '#A89CE8');
  const handleLogin = async () => {
    if (!formData.email || !formData.password) {
      toast({
        title: "Missing Fields",
        description: "Email and password are required.",
        status: "warning",
        position: "top",
      });
      return;
    }

    try {
      setLoading(true);

      await login(formData);

      toast({
        title: "Login Successful",
        status: "success",
        position: "top",
      });

      navigate("/admin/dashboards");
    } catch (error: any) {
      toast({
        title: "Login Failed",
        description:
          error?.response?.data?.message || "Invalid credentials",
        status: "error",
        position: "top",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box minH="100vh" bg={pageBg} position="relative" overflow="hidden">
      <Box
        position="absolute"
        top="0"
        left="0"
        w="100%"
        h="50%"
        bg="#7062B9"
      />
      <Flex minH="100vh" direction="column" position="relative">
        <Flex px={{ base: 6, md: 10 }} py={6}>
          <Text fontSize="lg" fontWeight="700" color="white">
            ServiceCall
          </Text>
        </Flex>

        <Flex flex="1" justify="center" align="center" px={4} py={8}>
          <Box
            bg={cardBg}
            borderRadius="25px"
            p={{ base: 6, md: 10 }}
            w="100%"
            maxW="420px"
            boxShadow="0 10px 30px rgba(112,98,185,0.12)"
          >
            <Heading fontSize="28px" mb="8px" color={textColor}>
              Sign In
            </Heading>

            <Text fontSize="sm" color="gray.500" mb="30px">
              Enter your email and password to sign in!
            </Text>

            {/* Email */}
            <FormControl mb="15px">
              <FormLabel fontSize="sm">Email *</FormLabel>
              <Input
                type="email"
                placeholder="you@email.com"
                bg={inputBg}
                border="1px solid"
                borderColor="transparent"
                borderRadius="14px"
                h="52px"
                fontSize="sm"
                color={textColor}
                value={formData.email}
                onChange={e =>
                  handleChange("email", e.target.value)
                }
                _focus={{
                  borderColor: "#7062B9",
                  boxShadow: "0 0 0 1px #7062B9",
                }}
              />
            </FormControl>

            {/* Password */}
            <FormControl mb="10px">
              <FormLabel fontSize="sm">Password *</FormLabel>
              <InputGroup>
                <Input
                  type={show ? "text" : "password"}
                  placeholder="Enter password"
                  bg={inputBg}
                  border="1px solid"
                  borderColor="transparent"
                  borderRadius="14px"
                  h="52px"
                  fontSize="sm"
                  color={textColor}
                  maxLength={16}
                  value={formData.password}
                  onChange={e =>
                    handleChange("password", e.target.value)
                  }
                  _focus={{
                    borderColor: "#7062B9",
                    boxShadow: "0 0 0 1px #7062B9",
                  }}
                />
                <InputRightElement h="100%">
                  <Icon
                    as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    cursor="pointer"
                    onClick={() => setShow(!show)}
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>

            <Flex justify="space-between" align="center" my="20px">
              <Checkbox size="sm">Keep me logged in</Checkbox>

              <NavLink to="/auth/forgot-password">
                <Text fontSize="sm" color={textColorBrand} fontWeight="600">
                  Forgot password?
                </Text>
              </NavLink>
            </Flex>

            <Button
              w="100%"
              h="50px"
              bg="#7062B9"
              color="white"
              borderRadius="14px"
              fontWeight="600"
              _hover={{ bg: "#5B4FA3" }}
              onClick={handleLogin}
              isLoading={loading}
            >
              Sign In
            </Button>

            <Flex
              flexDirection="column"
              justifyContent="center"
              alignItems="start"
              maxW="100%"
              mt="20px"
            >
              <Text color={textColorDetails} fontWeight="400" fontSize="14px">
                Not registered yet?
                <NavLink to="/auth/sign-up">
                  <Text
                    color={textColorBrand}
                    as="span"
                    ms="5px"
                    fontWeight="500"
                  >
                    Create an Account
                  </Text>
                </NavLink>
              </Text>
            </Flex>
          </Box>
        </Flex>

        <Text textAlign="center" fontSize="sm" color="gray.500" pb="20px">
          © 2026 ServiceCall
        </Text>
      </Flex>
    </Box>
  );
}

export default SignIn;
